import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCircle2,
  FileText,
  Info,
  Loader2,
  Lock,
  MessageSquareText,
  Quote,
  Send,
  X,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { API_BASE } from '../lib/api';
import { sesionApi, sesionKeys, type Aporte } from '../lib/sesion';
import { useColaMedia } from '../lib/colaMedia';
import { borrarGrabacion } from '../lib/grabacionEnCurso';
import { PulsarParaHablar } from '../components/PulsarParaHablar';
import { ColaAudio } from '../components/ColaAudio';
import { EsqueletoLineas } from '../components/ui/Esqueleto';
import { VisorDocumento } from '../components/validacion/VisorDocumento';
import {
  contextoDelBloque,
  documentosApi,
  documentosKeys,
  type Bloque,
  type SugerenciaBloque,
} from '../lib/documentos';
import type { GrabacionAudio } from '../hooks/useGrabadorAudio';

/**
 * VALIDACIÓN DE DOCUMENTOS — el módulo del link directo.
 *
 * La persona recibe el documento por fuera (correo, impreso) y ESTE link.
 * Al abrirlo entra con Google —la membresía al proceso se crea sola, mismo
 * mecanismo del QR de sala— y queda frente a una sola cosa: el nombre del
 * documento que está validando y el botón de hablar. Cada comentario (voz o
 * texto) cae en la actividad de ESE documento, con autor, hora y
 * transcripción automática. Tres documentos = tres links = tres destinos que
 * no se mezclan.
 *
 * No hay facilitador conduciendo: la actividad nace abierta y vive hasta que
 * el administrador la cierra (y congela el acta) o vence su fecha.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LOS DOS MODOS (V1 · anclaje a párrafo)
 *
 * 1. SIN DOCUMENTO EN ÁGORA — el modo que corre hoy en producción con las
 *    tres validaciones vivas del Consejo de la Carrera Judicial. El documento
 *    llegó por correo y acá solo se comenta «el documento». Este camino no
 *    cambió: mismo layout, mismo flujo, mismos textos. `GET /validaciones/
 *    {id}/documento` responde 404 y eso NO es un error que se le muestre a
 *    nadie: es la señal de que esta validación es de las de siempre.
 *
 * 2. CON DOCUMENTO EN ÁGORA — el texto se ve acá, se toca un párrafo y el
 *    comentario queda colgado de ese párrafo. El documento se desliza dentro
 *    de su propia caja y el párrafo señalado se lee JUSTO ARRIBA del botón de
 *    grabar, sin abrir ningún modal (SSOT §6.1). Si nadie eligió párrafo, el
 *    comentario se manda igual y queda sobre el documento completo: jamás se
 *    le pone una tranca a quien quiere hablar.
 *
 * El anclaje SIEMPRE ocurre después de que el aporte existe, con un endpoint
 * aparte. Si falla, el comentario ya está a salvo y queda «sin ancla», que es
 * un estado válido y previsto (SSOT §5.2, ancla huérfana). Se avisa con calma.
 */
export function ValidarPage() {
  const { actividadId } = useParams<{ actividadId: string }>();
  const [searchParams] = useSearchParams();
  const sala = searchParams.get('s') ?? '';

  const status = useAuthStore((s) => s.status);
  const hydrate = useAuthStore((s) => s.hydrate);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (status === 'idle') void hydrate();
  }, [status, hydrate]);

  if (!actividadId) return <MarcoInvitacion>Link incompleto: falta el documento.</MarcoInvitacion>;

  if (status === 'idle' || status === 'hydrating') {
    return (
      <MarcoInvitacion>
        <span className="flex items-center justify-center gap-2 text-content-muted">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Verificando tu sesión…
        </span>
      </MarcoInvitacion>
    );
  }

  if (status === 'anonymous') {
    return <PortonGoogle actividadId={actividadId} sala={sala} />;
  }

  return <Validador actividadId={actividadId} nombre={user?.nombre ?? ''} />;
}

/** Marco visual común: marca ÁGORA, tarjeta centrada, mobile-first. */
function MarcoInvitacion({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-surface-base">
      {/* Misma cabecera institucional del login: navy, logo negativo y el
          acento puntual del gradiente de marca (invitación = momento hero). */}
      <header className="relative bg-navy px-5 pb-5 pt-6">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-brand-gradient"
          aria-hidden="true"
        />
        <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-9 w-auto" />
        <p className="mt-2 font-sans text-sm text-white/80">
          Validación de documentos
        </p>
      </header>
      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-6">{children}</main>
      <footer className="px-4 pb-6 text-center font-sans text-xs text-content-muted">
        Grupo InteligencIA · tus comentarios quedan con tu nombre y su transcripción
      </footer>
    </div>
  );
}

/** Sin sesión: una sola acción — entrar con Google. El link ya trae la sala. */
function PortonGoogle({ actividadId, sala }: { actividadId: string; sala: string }) {
  const entrar = () => {
    const p = new URLSearchParams();
    if (sala) p.set('sala', sala);
    p.set('volver', `/validar/${actividadId}`);
    window.location.href = `${API_BASE}/auth/google/login?${p.toString()}`;
  };
  return (
    <MarcoInvitacion>
      <div className="rounded-xl border border-border bg-surface-raised p-6 text-center shadow-sm">
        <FileText className="mx-auto h-10 w-10 text-navy" aria-hidden="true" />
        <h1 className="mt-3 font-display text-xl font-semibold text-content">
          Te invitaron a validar un documento
        </h1>
        <p className="mt-2 font-sans text-sm leading-relaxed text-content-muted">
          El documento te llegó por aparte. Acá dejás tus comentarios —hablados o
          escritos— y quedan registrados con tu nombre, para que nada de lo que
          digas se pierda ni se atribuya a otra persona.
        </p>
        <button
          type="button"
          onClick={entrar}
          className="mt-5 w-full rounded-lg bg-navy px-4 py-3 font-sans text-base font-semibold text-white transition hover:bg-navy/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
        >
          Entrar con Google
        </button>
        <p className="mt-3 font-sans text-xs text-content-muted">
          Solo usamos tu cuenta para saber quién comenta. Nada se publica en tu
          nombre.
        </p>
      </div>
    </MarcoInvitacion>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ANCLAJE DE LA VOZ — por qué hace falta una libreta en el teléfono

   El texto es fácil: `postAporte` devuelve el aporte, así que su id está en la
   mano y se ancla en el acto. La voz NO: entra por la cola durable
   (`colaMedia`), que guarda el blob en IndexedDB y lo sube cuando puede —a
   veces minutos después, a veces tras recargar la página, a veces desde otra
   pestaña—. `encolarYEnviar` devuelve un booleano, no el aporte, así que en el
   momento de grabar el id todavía no existe en ninguna parte.

   La solución más simple que funciona, sin tocar la cola ni el endpoint de
   media: anotar en localStorage `{idem → bloque}` ANTES de encolar (misma
   escritura adelantada de colaMedia) y resolver el ancla cuando el aporte
   aparece. El poll de «tus comentarios» ya trae los aportes propios cada 15 s
   con su `payload.idem`, que es el mismo uuid del cliente. Al reconocerlo, se
   ancla y se tacha de la libreta. Sobrevive recargas, offline y cierres de
   pestaña, igual que el audio al que acompaña.
   ═══════════════════════════════════════════════════════════════════════ */

interface AnclaPendiente {
  bloque: string;
  ts: number;
}

/** Una semana: pasado eso, si el audio nunca subió, la nota ya no sirve. */
const VIDA_PENDIENTE_MS = 7 * 24 * 60 * 60 * 1000;
const claveAnclas = (actividadId: string) => `agora.anclas-voz.${actividadId}`;
/** Marca «esta validación sí trae documento», para poder mostrar su esqueleto. */
const claveConDocumento = (actividadId: string) => `agora.validacion-doc.${actividadId}`;

function leerPendientes(actividadId: string): Record<string, AnclaPendiente> {
  try {
    const crudo = window.localStorage.getItem(claveAnclas(actividadId));
    if (!crudo) return {};
    const datos = JSON.parse(crudo) as Record<string, AnclaPendiente>;
    const ahora = Date.now();
    return Object.fromEntries(
      Object.entries(datos).filter(([, v]) => v && ahora - v.ts < VIDA_PENDIENTE_MS),
    );
  } catch {
    return {};
  }
}

function escribirPendientes(actividadId: string, datos: Record<string, AnclaPendiente>): void {
  try {
    window.localStorage.setItem(claveAnclas(actividadId), JSON.stringify(datos));
  } catch {
    /* sin localStorage el audio igual se sube: solo se pierde el ancla */
  }
}

function anotarPendiente(actividadId: string, idem: string, bloque: string): void {
  const datos = leerPendientes(actividadId);
  datos[idem] = { bloque, ts: Date.now() };
  escribirPendientes(actividadId, datos);
}

function olvidarPendiente(actividadId: string, idem: string): void {
  const datos = leerPendientes(actividadId);
  delete datos[idem];
  escribirPendientes(actividadId, datos);
}

/* ── Chip de confirmación ────────────────────────────────────────────────────
   Qué comentarios ya pasaron por el chip, para no volver a preguntar por el
   mismo. Vive en el navegador porque la pregunta es de esta persona y de este
   momento: si alguien recarga, no queremos que el chip resucite preguntando
   por un comentario que ya resolvió hace media hora. */
const claveChip = (actividadId: string) => `agora.validacion-chip.${actividadId}`;

function yaPreguntado(actividadId: string, aporteId: string): boolean {
  try {
    const crudo = window.localStorage.getItem(claveChip(actividadId));
    return crudo ? (JSON.parse(crudo) as string[]).includes(aporteId) : false;
  } catch {
    return false;
  }
}

function marcarPreguntado(actividadId: string, aporteId: string): void {
  try {
    const crudo = window.localStorage.getItem(claveChip(actividadId));
    const previos = crudo ? (JSON.parse(crudo) as string[]) : [];
    if (previos.includes(aporteId)) return;
    // Se guardan los últimos 200: una validación larga no debe llenar el
    // almacenamiento del teléfono con identificadores viejos.
    window.localStorage.setItem(
      claveChip(actividadId),
      JSON.stringify([...previos, aporteId].slice(-200)),
    );
  } catch {
    /* sin localStorage el chip vuelve a preguntar; molesta, no rompe */
  }
}

/** Con sesión: el documento, el botón de hablar, el texto y lo ya aportado. */
function Validador({ actividadId, nombre }: { actividadId: string; nombre: string }) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [avisoVoz, setAvisoVoz] = useState<string | null>(null);
  /** Párrafo elegido (id_estable). null = el comentario va sobre todo el documento. */
  const [bloqueSel, setBloqueSel] = useState<string | null>(null);
  const [avisoAncla, setAvisoAncla] = useState<string | null>(null);

  const actividad = useQuery({
    queryKey: sesionKeys.actividad(actividadId),
    queryFn: () => sesionApi.actividad(actividadId),
    refetchInterval: 30_000,
  });

  const mios = useQuery({
    queryKey: [...sesionKeys.aportes(actividadId), 'mios'],
    queryFn: () => sesionApi.aportes(actividadId, undefined, true),
    refetchInterval: 15_000,
  });

  /**
   * El documento, si esta validación lo tiene cargado en ÁGORA. El cliente ya
   * traduce el 404 a `null`: «no lo tiene» es un dato, no una falla, y no se
   * muestra ningún error en pantalla. `retry: false` para no golpear tres
   * veces cuando la respuesta es un no.
   */
  const documento = useQuery({
    queryKey: documentosKeys.deValidacion(actividadId),
    queryFn: () => documentosApi.deValidacion(actividadId),
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const bloques: Bloque[] = useMemo(
    () => documento.data?.bloques ?? [],
    [documento.data],
  );
  const hayDocumento = bloques.length > 0;

  /* Solo si ya vimos el documento alguna vez mostramos su esqueleto: así las
     validaciones sin documento no estrenan un parpadeo que hoy no existe. */
  const [esperabaDocumento] = useState(() => {
    try {
      return window.localStorage.getItem(claveConDocumento(actividadId)) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!hayDocumento) return;
    try {
      window.localStorage.setItem(claveConDocumento(actividadId), '1');
    } catch {
      /* el esqueleto es un lujo: sin localStorage se vive igual */
    }
  }, [hayDocumento, actividadId]);

  const bloqueElegido = useMemo(
    () => bloques.find((b) => b.id_estable === bloqueSel) ?? null,
    [bloques, bloqueSel],
  );

  /** El aporte ya está a salvo; el ancla es lo que puede fallar. Se dice sin drama. */
  const avisarAnclaFallida = useCallback(() => {
    setAvisoAncla(
      'Tu comentario quedó registrado, pero no logramos señalar el párrafo: entra como comentario del documento completo.',
    );
  }, []);

  const enviarTexto = useMutation({
    mutationFn: async () => {
      const aporte = await sesionApi.postAporte(actividadId, {
        tipo: 'texto',
        payload: {
          texto: texto.trim(),
          idem: crypto.randomUUID(),
          // El párrafo elegido viaja TAMBIÉN dentro del aporte, que es
          // insert-only: es el rastro inmutable de a qué quiso referirse quien
          // comentó. El ancla de al lado se puede mover —un facilitador puede
          // reubicar el comentario— y entonces esta huella es lo único que
          // conserva la intención original.
          ...(bloqueSel ? { bloque_id_estable: bloqueSel } : {}),
        },
      });
      // El ancla va DESPUÉS y por su propio endpoint: el aporte es insert-only
      // (C1) y nunca se toca. `origen` lo pone el servidor: 'puntero' cuando lo
      // manda quien escribió el comentario. Mandamos también huella, prefijo y
      // sufijo —los tres selectores redundantes del SSOT §5.2— para que el
      // ancla pueda re-anclarse el día que el documento cambie de versión.
      if (bloqueSel && bloqueElegido) {
        try {
          await documentosApi.anclar(actividadId, {
            aporte_id: aporte.id,
            bloque_id_estable: bloqueSel,
            ...contextoDelBloque(bloqueElegido.texto),
          });
          setAvisoAncla(null);
        } catch {
          avisarAnclaFallida();
        }
      }
      return aporte;
    },
    onSuccess: () => {
      setTexto('');
      void qc.invalidateQueries({ queryKey: sesionKeys.aportes(actividadId) });
    },
  });

  /** La voz entra por la misma cola durable del Bolsillo: sobrevive recargas. */
  const subirVoz = useCallback(
    async (g: GrabacionAudio) => {
      setAvisoVoz(null);
      // Libreta primero (escritura adelantada): si la pestaña muere entre el
      // encolado y la subida, el ancla sigue esperando a su aporte.
      if (bloqueSel) anotarPendiente(actividadId, g.id, bloqueSel);
      const ok = await useColaMedia.getState().encolarYEnviar({
        idem: g.id,
        actividadId,
        tipo: 'audio',
        blob: g.blob,
        filename: `comentario.${g.ext}`,
        mime: g.mime,
        inicio: g.inicioISO,
        fin: g.finISO,
        duracionS: g.duracionS,
        // Mismo rastro inmutable que en el texto: el párrafo elegido queda
        // dentro del aporte, además de en el ancla que sí se puede mover.
        ...(bloqueSel ? { campos: { bloque_id_estable: bloqueSel } } : {}),
      });
      void borrarGrabacion(g.id);
      if (ok) {
        setAvisoVoz('Comentario recibido: se transcribe en unos segundos.');
        void qc.invalidateQueries({ queryKey: sesionKeys.aportes(actividadId) });
      } else {
        setAvisoVoz(
          'Sin conexión ahora: tu voz quedó guardada en este teléfono y se subirá sola.',
        );
      }
    },
    [actividadId, qc, bloqueSel],
  );

  const listaMios = useMemo(() => {
    const l = mios.data?.aportes ?? [];
    return [...l].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }, [mios.data]);

  /**
   * Cierre del anclaje diferido: cada vez que llegan los aportes propios se
   * buscan los `idem` anotados en la libreta y se anclan. Reintenta solo en el
   * siguiente poll si el servidor no contestó; mientras tanto el comentario
   * existe y está sin ancla, que es un estado previsto.
   */
  const anclandoVoz = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!hayDocumento) return;
    const aportes = mios.data?.aportes;
    if (!aportes?.length) return;
    const pendientes = leerPendientes(actividadId);
    if (Object.keys(pendientes).length === 0) return;
    for (const a of aportes) {
      const idem = (a.payload as { idem?: string } | undefined)?.idem;
      if (!idem) continue;
      const pendiente = pendientes[idem];
      if (!pendiente || anclandoVoz.current.has(idem)) continue;
      const bloque = bloques.find((b) => b.id_estable === pendiente.bloque);
      anclandoVoz.current.add(idem);
      void documentosApi
        .anclar(actividadId, {
          aporte_id: a.id,
          bloque_id_estable: pendiente.bloque,
          ...(bloque ? contextoDelBloque(bloque.texto) : {}),
        })
        .then(() => olvidarPendiente(actividadId, idem))
        .catch(() => {
          /* se reintenta en el próximo poll; el comentario ya está a salvo */
        })
        .finally(() => anclandoVoz.current.delete(idem));
    }
  }, [mios.data, actividadId, hayDocumento]);

  /**
   * EL CHIP DE CONFIRMACIÓN (SSOT §5.3).
   *
   * Mucha gente habla nombrando el párrafo —«en cuanto al RF 64…»— sin haberlo
   * tocado antes. El léxico del documento reconoce esa mención sin gastar una
   * corrida de IA, y acá se le pregunta a la persona si acertó. Confirmar toma
   * dos segundos y deja el comentario ubicado; en la primera validación real
   * del Consejo, seis de doce comentarios habrían quedado en su párrafo así.
   *
   * Se pregunta de a uno y solo por lo que todavía no tiene dirección. Un chip
   * que insiste, o que propone cualquier cosa con tal de proponer algo, enseña
   * a ignorarlo — y entonces deja de servir para siempre.
   */
  const [chip, setChip] = useState<{ aporteId: string; sug: SugerenciaBloque } | null>(null);
  const consultandoChip = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!hayDocumento || chip) return;
    const aportes = mios.data?.aportes;
    if (!aportes?.length) return;
    const conBloquePendiente = new Set(Object.keys(leerPendientes(actividadId)));

    // Del más reciente al más viejo: lo que la persona acaba de decir es lo
    // que todavía tiene fresco y lo único que puede confirmar sin releer.
    for (const a of [...aportes].reverse()) {
      const payload = (a.payload ?? {}) as { texto?: string; idem?: string; bloque_id_estable?: string };
      // Ya se envió apuntando a un párrafo: no hay nada que preguntar.
      if (payload.bloque_id_estable) continue;
      if (payload.idem && conBloquePendiente.has(payload.idem)) continue;
      const dicho = payload.texto ?? a.transcripcion?.texto;
      if (!dicho?.trim()) continue; // una nota de voz sin transcribir todavía
      if (consultandoChip.current.has(a.id) || yaPreguntado(actividadId, a.id)) continue;

      consultandoChip.current.add(a.id);
      void documentosApi
        .sugerirBloque(actividadId, dicho)
        .then((sugerencias) => {
          const mejor = sugerencias[0];
          if (mejor && mejor.confianza >= 0.5) setChip({ aporteId: a.id, sug: mejor });
          else marcarPreguntado(actividadId, a.id); // no hay nada que ofrecer
        })
        .catch(() => {
          /* sin sugerencia el comentario queda sin ubicar, que es un estado
             previsto: nunca se pierde lo dicho */
        })
        .finally(() => consultandoChip.current.delete(a.id));
      break; // uno por vez
    }
  }, [mios.data, actividadId, hayDocumento, chip]);

  const responderChip = useCallback(
    (aceptar: boolean) => {
      if (!chip) return;
      marcarPreguntado(actividadId, chip.aporteId);
      const { aporteId, sug } = chip;
      setChip(null);
      if (!aceptar) return;
      void documentosApi
        .anclar(actividadId, {
          aporte_id: aporteId,
          bloque_id_estable: sug.bloque_id_estable,
          ...contextoDelBloque(sug.texto),
        })
        .then(() => {
          setAvisoAncla(null);
          void qc.invalidateQueries({ queryKey: sesionKeys.aportes(actividadId) });
        })
        .catch(() => avisarAnclaFallida());
    },
    [chip, actividadId, qc, avisarAnclaFallida],
  );

  const abierta = actividad.data?.estado === 'abierta';
  const titulo = actividad.data?.titulo ?? 'Validación de documento';
  const cierraEl = (actividad.data as { cierra_el?: string | null } | undefined)?.cierra_el ?? null;

  if (actividad.isLoading) {
    return (
      <MarcoInvitacion>
        <span className="flex items-center justify-center gap-2 text-content-muted">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Abriendo la validación…
        </span>
      </MarcoInvitacion>
    );
  }
  if (actividad.isError || !actividad.data) {
    return (
      <MarcoInvitacion>
        <div className="rounded-xl border border-border bg-surface-raised p-6 text-center">
          <p className="font-sans text-sm text-content">
            No encontramos esta validación. Revisá que el link esté completo, o
            pedile uno nuevo a quien te invitó.
          </p>
        </div>
      </MarcoInvitacion>
    );
  }

  const cajaTexto = (
    <CajaTexto
      valor={texto}
      onCambio={setTexto}
      onEnviar={() => enviarTexto.mutate()}
      enviando={enviarTexto.isPending}
      error={enviarTexto.isError}
      pie={
        hayDocumento ? (
          <p className="mt-2 font-sans text-xs text-content-muted">
            {bloqueElegido
              ? `Se guarda señalando ${etiquetaBloque(bloqueElegido)}.`
              : 'Sin párrafo señalado: se guarda como comentario del documento completo.'}
          </p>
        ) : undefined
      }
    />
  );

  return (
    <MarcoInvitacion>
      {/* El documento que se valida — el link decide el destino */}
      <div className="rounded-xl border border-border bg-surface-raised p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <FileText className="mt-0.5 h-6 w-6 shrink-0 text-navy" aria-hidden="true" />
          <div className="min-w-0">
            <h1 className="font-display text-lg font-semibold leading-snug text-content">
              {titulo}
            </h1>
            <p className="mt-0.5 font-sans text-xs text-content-muted">
              Comentás como <b>{nombre || 'tu cuenta de Google'}</b>
              {cierraEl ? ` · recibe comentarios hasta el ${cierraEl}` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* El texto del documento, cuando está cargado en ÁGORA. Si no lo está,
          acá no se renderiza nada y la página queda como siempre. */}
      {documento.isLoading && esperabaDocumento && (
        <div className="mt-4 rounded-xl border border-border bg-surface-raised p-4 shadow-sm">
          <EsqueletoLineas lineas={8} />
        </div>
      )}
      {hayDocumento && (
        /* El documento se desliza DENTRO de su caja, no arrastrando la página:
           así el párrafo señalado y el botón de hablar no se van de la pantalla
           cada vez que se busca algo más abajo (SSOT §6.1). La cabecera
           pegajosa del visor se pega al borde de esta caja. */
        <div className="mt-4 max-h-[55vh] overflow-y-auto overscroll-contain rounded-xl">
          <VisorDocumento
            bloques={bloques}
            seleccionado={bloqueSel}
            onSeleccionar={(id) => {
              setBloqueSel(id);
              setAvisoAncla(null);
            }}
            /* Cerrada la validación se lee, no se señala: ofrecer «señalar»
               sería ofrecer algo que ya no se puede hacer. */
            soloLectura={!abierta}
            /* La tira de abajo ya recuerda el párrafo elegido mientras se
               graba; repetirlo en la cabecera del visor sería ruido. */
            mostrarSeleccion={false}
          />
        </div>
      )}

      {/* El chip: aparece solo si hay algo concreto que confirmar, y ofrece las
          dos salidas con el mismo peso. Va arriba de la captura porque se
          responde de un vistazo, sin dejar de ver lo que se está por decir. */}
      {abierta && chip && (
        <div className="mt-4 rounded-xl border border-primary-600 bg-surface-raised p-3.5 shadow-sm">
          <p className="font-sans text-sm text-content">
            ¿Hablabas del{' '}
            <strong className="font-semibold text-navy">{chip.sug.ref}</strong>?
          </p>
          <p className="mt-1 line-clamp-2 font-sans text-xs leading-relaxed text-content-muted">
            {chip.sug.texto}
          </p>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              onClick={() => responderChip(true)}
              className="inline-flex min-h-touch flex-1 items-center justify-center gap-1.5 rounded-lg bg-navy px-3 font-sans text-sm font-semibold text-content-inverse"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              Sí, es ese
            </button>
            <button
              type="button"
              onClick={() => responderChip(false)}
              className="inline-flex min-h-touch items-center justify-center rounded-lg border border-border px-4 font-sans text-sm font-semibold text-content"
            >
              No
            </button>
          </div>
        </div>
      )}

      {!abierta ? (
        <div className="mt-4 rounded-xl border border-border bg-surface-raised p-5 text-center">
          <Lock className="mx-auto h-7 w-7 text-content-muted" aria-hidden="true" />
          <p className="mt-2 font-sans text-sm text-content">
            Esta validación ya cerró y sus comentarios pasaron al acta.
          </p>
          <p className="mt-1 font-sans text-xs text-content-muted">
            Lo que aportaste quedó registrado con tu nombre.
          </p>
        </div>
      ) : hayDocumento ? (
        /* MODO CON DOCUMENTO: misma tarjeta de voz de siempre, con el párrafo
           señalado leyéndose JUSTO ARRIBA del botón mientras se graba. Sin
           modal y sin barra flotante: el grabador mide 17rem fijos y anclarlo
           al borde inferior le comería media pantalla al documento. */
        <>
          <div className="mt-4 rounded-xl border border-border bg-surface-raised p-4 shadow-sm">
            <TiraSeleccion bloque={bloqueElegido} onQuitar={() => setBloqueSel(null)} />
            <PulsarParaHablar actividadId={actividadId} onListo={subirVoz} bloqueado={null} />
            {avisoVoz && (
              <p className="mt-2 flex items-center gap-1.5 font-sans text-xs text-content-muted">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                {avisoVoz}
              </p>
            )}
            <ColaAudio />
          </div>

          {cajaTexto}

          {avisoAncla && (
            <p className="mt-2 flex items-start gap-1.5 font-sans text-xs text-content-muted">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {avisoAncla}
            </p>
          )}
        </>
      ) : (
        <>
          {/* Voz: el gesto principal, idéntico al Bolsillo */}
          <div className="mt-4 rounded-xl border border-border bg-surface-raised p-4 shadow-sm">
            <p className="mb-2 font-sans text-sm font-medium text-content">
              Dejá tu comentario hablado
            </p>
            <PulsarParaHablar actividadId={actividadId} onListo={subirVoz} bloqueado={null} />
            {avisoVoz && (
              <p className="mt-2 flex items-center gap-1.5 font-sans text-xs text-content-muted">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                {avisoVoz}
              </p>
            )}
            <ColaAudio />
          </div>

          {/* Texto: la alternativa silenciosa */}
          {cajaTexto}
        </>
      )}

      {/* Lo que YO ya dije de este documento — la prueba de que llegó */}
      <section className="mt-4">
        <h2 className="mb-2 flex items-center gap-2 font-sans text-sm font-semibold text-content">
          <MessageSquareText className="h-4 w-4 text-navy" aria-hidden="true" />
          Tus comentarios sobre este documento ({listaMios.length})
        </h2>
        {listaMios.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-3 font-sans text-sm text-content-muted">
            Todavía no has comentado. Lo que digas aparecerá acá, con su
            transcripción.
          </p>
        ) : (
          <ul className="space-y-2">
            {listaMios.map((a) => (
              <MiComentario key={a.id} aporte={a} />
            ))}
          </ul>
        )}
      </section>
    </MarcoInvitacion>
  );
}

/** Cómo se nombra un párrafo en una frase: su numeración si la tiene. */
function etiquetaBloque(b: Bloque): string {
  if (b.ref) return `«${b.ref}»`;
  if (b.tipo === 'titulo') return 'ese título';
  return 'ese párrafo';
}

/**
 * El fragmento señalado, siempre a la vista mientras se graba. Sin selección
 * no se esconde: explica en una línea qué pasa si se habla sin señalar nada.
 */
function TiraSeleccion({
  bloque,
  onQuitar,
}: {
  bloque: Bloque | null;
  onQuitar: () => void;
}) {
  if (!bloque) {
    return (
      <p className="flex items-start gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 font-sans text-xs leading-relaxed text-content-muted">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Tocá un párrafo arriba para señalarlo. Si preferís, hablá sin señalar
        nada: tu comentario queda sobre el documento completo.
      </p>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-lg border border-primary bg-primary/10 px-3 py-2">
      <Quote className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-sans text-xs font-semibold text-content">
          Comentando sobre{bloque.ref ? ` ${bloque.ref}` : ''}
        </p>
        <p className="line-clamp-2 font-sans text-xs leading-relaxed text-content-muted">
          {bloque.texto}
        </p>
      </div>
      <button
        type="button"
        onClick={onQuitar}
        aria-label="Quitar el párrafo señalado"
        className="-my-1 -mr-1 flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted transition-colors duration-fast hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * La caja de texto: la alternativa silenciosa. Es la misma en los dos modos —
 * `pie` es lo único que cambia, y sin documento no se renderiza.
 */
function CajaTexto({
  valor,
  onCambio,
  onEnviar,
  enviando,
  error,
  pie,
}: {
  valor: string;
  onCambio: (v: string) => void;
  onEnviar: () => void;
  enviando: boolean;
  error: boolean;
  pie?: React.ReactNode;
}) {
  return (
    <form
      className="mt-3 rounded-xl border border-border bg-surface-raised p-4 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (valor.trim().length >= 3) onEnviar();
      }}
    >
      <label
        htmlFor="comentario"
        className="mb-2 block font-sans text-sm font-medium text-content"
      >
        …o escribilo
      </label>
      <textarea
        id="comentario"
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        rows={3}
        placeholder="Ejemplo: en el requisito RF-041, la autorización del préstamo también la da la Secretaría Ejecutiva."
        className="w-full resize-y rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content placeholder:text-content-muted focus:border-navy focus:outline-none"
      />
      <button
        type="submit"
        disabled={valor.trim().length < 3 || enviando}
        className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-navy px-4 py-2 font-sans text-sm font-semibold text-white transition hover:bg-navy/90 disabled:opacity-40"
      >
        {enviando ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Send className="h-4 w-4" aria-hidden="true" />
        )}
        Enviar comentario
      </button>
      {error && (
        <p className="mt-2 font-sans text-xs text-error">
          No se pudo enviar. Probá de nuevo; si estás sin señal, usá la voz:
          queda guardada y se sube sola.
        </p>
      )}
      {pie}
    </form>
  );
}

function MiComentario({ aporte }: { aporte: Aporte }) {
  const payload = (aporte.payload ?? {}) as Record<string, unknown>;
  const texto =
    (payload.texto as string | undefined) ??
    aporte.transcripcion?.texto ??
    null;
  const esVoz = aporte.tipo === 'audio';
  const hora = new Date(aporte.created_at).toLocaleTimeString('es-GT', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <li className="rounded-lg border border-border bg-surface-raised px-4 py-3">
      <p className="font-sans text-xs text-content-muted">
        {esVoz ? 'Nota de voz' : 'Texto'} · {hora}
      </p>
      {texto ? (
        <p className="mt-1 font-sans text-sm leading-relaxed text-content">{texto}</p>
      ) : (
        <p className="mt-1 flex items-center gap-1.5 font-sans text-sm italic text-content-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          Transcribiendo tu voz…
        </p>
      )}
    </li>
  );
}
